import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CarFront,
  Cog,
  Copy,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
} from "lucide-react";

import { 
  Car, 
  User, 
  MapPin, 
  Settings, 
  ShieldCheck, 
  FileText,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';

import { isAlphanumeric } from "validator";
import { useService } from "../../../../context/ServiceContext";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import { fetchProfile } from "../../../../Services/profile/profileservice";
import Seo from "../../../../Components/SeoComponent/Seo";
import Header from "../../../../Components/Header";
import {
  getEngineToRcSrv2,
} from "../../../../Services/ApiTestService/apiTestService";

function EngineToRcSrv2() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [engineNo, setEngineNo] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleEngineToRc = async () => {
    if (!profileData) return;

    const validateEngineNum = isAlpha(engineNo);

    if (!engineNo) {
      return setInputError("Engine Number is Required");
    }

    if (engineNo && !validateEngineNum) {
      return setInputError(
        "⚠️ Please enter alpha numeric in Engine No. field",
      );
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await getEngineToRcSrv2({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        engine_number: engineNo.trim()?.toUpperCase(),
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <CarFront size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {serviceName}
              </h2>
              <p className="text-sm text-slate-500">
                Verify vehicle details using the engine number.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6  gap-6 flex justify-center items-center">
            {/* Engine Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Cog size={17} className="text-blue-600" />
                Engine Number
              </label>

              <input
                type="text"
                value={engineNo}
                onChange={(e) => setEngineNo(e.target.value)}
                placeholder="Eg: K10C****4749"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Button */}
            <div className="mt-8 flex ">
              <button
                disabled={isLoading || !engineNo}
                onClick={handleEngineToRc}
                className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Vehicle
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Engine Number. Clicking the Verify button.
              After successful verification, the system will return the response
              in structured format, including contact details associated with
              the provided details. You may review and copy the data for your
              reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-2" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-2 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                Challan Details
              </h2>

              {response?.status?.code === 200 && (
                <RCDetailsDashboard response={response}/>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default EngineToRcSrv2;



const RCDetailsDashboard = ({ response }) => {
  // Safe exit if data is not available
  if (response?.status?.code !== 200 || !response?.data) return null;

  const { data } = response;

  // Helper to check if a document is expired based on current date
  const isExpired = (dateString) => {
    if (!dateString) return false;
    const expiry = new Date(dateString);
    const today = new Date();
    return expiry < today;
  };
  
  // Reusable component for small data blocks
  const InfoBlock = ({ label, value, highlight = false, hasError = false }) => (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm 
          ${hasError ? 'font-bold text-red-600' : 
            highlight ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`
        }>
          {value || 'N/A'}
        </span>
        {hasError && <AlertTriangle size={14} className="text-red-500" />}
      </div>
    </div>
  );

  // Safe string lowercase helper to prevent null errors
  const safeLower = (str) => str ? str.toLowerCase() : '';

  return (
    <div className="max-w-6xl mx-auto space-y-6 font-sans">
      
      {/* 1. Primary Hero Banner */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-100 flex flex-col items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl shadow-inner">
              <Car size={32} />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  {data.regNo}
                </h2>
                <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 uppercase tracking-wide
                  ${data.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {data.status === 'ACTIVE' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                  {data.status}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-600 mt-1 capitalize">
                {safeLower(data.vehicleManufacturerName)} • {safeLower(data.model)} • {data.type}
              </p>
            </div>
          </div>
          
          <div className="text-left border-t md:border-t-0 pt-4 md:pt-0">
            <p className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center md:justify-end gap-1.5">
              <User size={14} /> Registered Owner ({data.ownerCount > 1 ? `${data.ownerCount}nd Owner` : '1st Owner'})
            </p>
            <p className="text-lg font-bold text-gray-900 capitalize">
              {safeLower(data.owner)}
            </p>
            {data.ownerFatherName && (
              <p className="text-xs text-gray-500 capitalize">
                C/O: {safeLower(data.ownerFatherName)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 2. Detailed Data Grid */}
      <div className="grid grid-cols-1  gap-6">
        
        {/* Registration & RTO Details */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <MapPin size={18} className="text-blue-500" />
            <h3 className="text-base font-bold text-gray-800">Registration Details</h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <InfoBlock label="Authority (RTO)" value={data.regAuthority} />
            <InfoBlock label="Vehicle Class" value={data.vehicleClass} />
            <InfoBlock label="Registration Date" value={data.regDate} />
            <InfoBlock label="RC Expiry" value={data.rcExpiryDate} highlight hasError={isExpired(data.rcExpiryDate)} />
            <div className="col-span-2">
              <InfoBlock label="Address" value={data.presentAddress} />
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <Settings size={18} className="text-gray-500" />
            <h3 className="text-base font-bold text-gray-800">Vehicle Specifications</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-4">
            <div className="col-span-2 sm:col-span-3">
              <InfoBlock label="Chassis Number" value={data.chassis} />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <InfoBlock label="Engine Number" value={data.engine} />
            </div>
            <InfoBlock label="Color" value={data.vehicleColour} />
            <InfoBlock label="Body Type" value={data.bodyType} />
            <InfoBlock label="Norms" value={data.normsType} />
            <InfoBlock label="Engine Cap (CC)" value={`${data.vehicleCubicCapacity} cc`} />
            <InfoBlock label="Seating Cap" value={data.vehicleSeatCapacity} />
            <InfoBlock label="Cylinders" value={data.vehicleCylindersNo} />
          </div>
        </div>

        {/* Compliance & Insurance */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <ShieldCheck size={18} className="text-green-500" />
            <h3 className="text-base font-bold text-gray-800">Compliance & Insurance</h3>
          </div>
          <div className="grid grid-cols-2 gap-y-5 gap-x-6">
            <div>
              <InfoBlock label="Insurance Company" value={data.vehicleInsuranceCompanyName} />
              <p className="text-xs text-gray-500 mt-1 font-mono">{data.vehicleInsurancePolicyNumber || 'No Policy Number'}</p>
            </div>
            <InfoBlock 
              label="Insurance Valid Upto" 
              value={data.vehicleInsuranceUpto} 
              highlight 
              hasError={isExpired(data.vehicleInsuranceUpto)} 
            />
            
            <InfoBlock label="PUCC Number" value={data.puccNumber} />
            <InfoBlock 
              label="PUCC Valid Upto" 
              value={data.puccUpto} 
              highlight 
              hasError={isExpired(data.puccUpto)} 
            />
            
            <InfoBlock 
              label="Road Tax Paid Upto" 
              value={data.vehicleTaxUpto} 
              highlight 
              hasError={isExpired(data.vehicleTaxUpto)} 
            />
          </div>
        </div>

        {/* Finance & Permit Details */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
            <FileText size={18} className="text-purple-500" />
            <h3 className="text-base font-bold text-gray-800">Finance & Weight</h3>
          </div>
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <div className="col-span-2">
              <InfoBlock label="Financer" value={data.rcFinancer || 'None (Cash/Fully Paid)'} />
            </div>
            
            <InfoBlock label="Commercial Vehicle" value={data.isCommercial ? 'Yes' : 'No'} />
            <InfoBlock label="Permit Details" value={data.permitType || 'Not Applicable'} />
            
            <InfoBlock label="Gross Weight" value={`${data.grossVehicleWeight} kg`} />
            <InfoBlock label="Unladen Weight" value={`${data.unladenWeight} kg`} />
          </div>
        </div>

      </div>
    </div>
  );
};

